import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, ShieldCheck, CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

const cardStyle = { background: 'rgba(9, 46, 26, 0.45)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)' };
const bgStyle = { background: 'radial-gradient(120% 90% at 50% -10%, #1f7a3d 0%, #0d5327 34%, #063217 62%, #02160c 100%)' };
const inputCls = 'w-full h-14 pl-12 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-emerald-100/35 outline-none focus:border-emerald-400/60 focus:bg-white/10 transition-all';
const btnCls = 'w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-lg font-bold shadow-lg shadow-emerald-500/30 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // request | verify | done
  const [identifier, setIdentifier] = useState('');
  const [channels, setChannels] = useState([]);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const requestCode = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { setError('Please enter your email, username or phone.'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/password-reset/request`, { identifier: identifier.trim() });
      const chans = res.data?.channels || [];
      if (chans.length === 0) {
        // Nothing was delivered (no channel configured or account not found)
        setError(res.data?.message || 'We could not send a code. Please contact support.');
        return;
      }
      setChannels(chans);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not process your request. Please try again.');
    } finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { setError('Enter the 6-digit code we sent you.'); return; }
    if (newPassword.length < 6) { setError('New password must be at least 6 characters.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/auth/password-reset/verify`, {
        identifier: identifier.trim(), code: code.trim(), new_password: newPassword,
      });
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
    } finally { setLoading(false); }
  };

  const destinationText = channels.length
    ? `We sent a 6-digit code to ${channels.map((c) => c.to).join(' and ')}.`
    : 'If an account matches, a 6-digit code has been sent.';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-10 relative overflow-hidden" style={bgStyle} data-testid="forgot-password-page">
      <div className="pointer-events-none absolute -top-24 right-[-60px] w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-80px] left-[-40px] w-80 h-80 rounded-full bg-emerald-600/20 blur-3xl" />

      <div className="w-full max-w-md relative">
        <button
          onClick={() => (step === 'verify' ? setStep('request') : navigate('/login'))}
          data-testid="forgot-back-button"
          className="flex items-center gap-2 text-emerald-100/70 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" /> {step === 'verify' ? 'Change details' : 'Back to login'}
        </button>

        <div className="rounded-[32px] border border-white/10 px-7 py-9 shadow-[0_20px_60px_rgba(0,0,0,0.45)]" style={cardStyle}>
          {step === 'done' ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-9 h-9 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-extrabold text-white mb-2">Password reset</h1>
              <p className="text-emerald-100/70 text-sm leading-relaxed">Your password has been updated. You can now log in with your new password.</p>
              <button onClick={() => navigate('/login')} data-testid="forgot-return-login" className={`${btnCls} mt-7`}>
                Return to login
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-extrabold text-white tracking-tight">
                  {step === 'request' ? 'Reset Password' : 'Enter Code'}
                </h1>
                <p className="text-emerald-100/70 mt-2 text-sm">
                  {step === 'request'
                    ? "Enter your account email or phone number and we'll send you a reset code."
                    : destinationText}
                </p>
              </div>

              {error && (
                <div className="mb-5 bg-red-500/15 border border-red-400/30 rounded-2xl p-3.5 flex items-start gap-2.5" data-testid="forgot-error">
                  <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-100">{error}</p>
                </div>
              )}

              {step === 'request' ? (
                <form onSubmit={requestCode} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-emerald-50/90 mb-2 ml-1">Email or Phone Number</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-200/60" />
                      <input type="text" value={identifier} onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
                        placeholder="Email or phone number" data-testid="forgot-identifier-input" className={inputCls} disabled={loading} />
                    </div>
                  </div>
                  <button type="submit" disabled={loading} data-testid="forgot-submit-button" className={btnCls}>
                    {loading ? <><span className="w-5 h-5 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />Sending…</> : 'Send reset code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={verifyCode} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-emerald-50/90 mb-2 ml-1">6-digit code</label>
                    <div className="relative">
                      <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-200/60" />
                      <input type="text" inputMode="numeric" maxLength={6} value={code}
                        onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                        placeholder="123456" data-testid="forgot-code-input"
                        className={`${inputCls} tracking-[0.5em] font-bold`} disabled={loading} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-emerald-50/90 mb-2 ml-1">New password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-200/60" />
                      <input type={showPassword ? 'text' : 'password'} value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                        placeholder="At least 6 characters" data-testid="forgot-newpassword-input"
                        className="w-full h-14 pl-12 pr-12 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-emerald-100/35 outline-none focus:border-emerald-400/60 focus:bg-white/10 transition-all" disabled={loading} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="forgot-toggle-password"
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-200/60 hover:text-emerald-100">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} data-testid="forgot-verify-button" className={btnCls}>
                    {loading ? <><span className="w-5 h-5 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />Resetting…</> : 'Reset password'}
                  </button>
                  <button type="button" onClick={requestCode} disabled={loading} data-testid="forgot-resend-button"
                    className="w-full text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors">
                    Didn't get it? Resend code
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 mt-7 text-emerald-100/35">
          <ShieldCheck className="w-4 h-4" />
          <span className="text-xs font-semibold tracking-[0.15em]">SECURED BY YACHANGU</span>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
